import { IListObjectStore } from '../../_common/type';
import { IJobHOCState } from '../../_core/job/types';
import {
    EMARPOLDocuments,
    TMARPOLProcessedTank,
    TMARPOLTank,
} from '../marpol/@types';
import { TAnnex6V2GroupA } from './groups/group-a';
import { TAnnex6V2GroupB } from './groups/group-b';
import { TAnnex6V2GroupC } from './groups/group-c';
import { TAnnex6V2GroupD } from './groups/group-d';
import { TAnnex6V2GroupE } from './groups/group-e';
import { TAnnex6V2GroupF } from './groups/group-f';
import { TAnnex6V2GroupG } from './groups/group-g';
import { TAnnex6V2GroupH } from './groups/group-h';
import { TAnnex6V2GroupI } from './groups/group-i';
import { TAnnex6V2GroupJ } from './groups/group-j';
import { TAnnex6V2GroupK } from './groups/group-k';
import { TAnnex6V2GroupL } from './groups/group-l';
import { TAnnex6V2GroupM } from './groups/group-m';

export enum EAnnex6V2OperationState {
    RECORD = 'RECORD',
    PREVIEW = 'PREVIEW',
}

export enum EAnnex6V2RecordState {
    APPROVED = 'APPROVED',
    STRIKED_OFF = 'STRIKED-OFF',
    SUBMITTED = 'SUBMITTED',
    IDLE = 'IDLE',
    EDIT = 'EDIT',
    VERIFIED = 'VERIFIED',
}

export type TAnnex6V2RecordUser = {
    name: string;
    designation: string;
    staffId: string;
    date: string;
};

export type TAnnex6V2CommonFields = {
    selectedDate: string;
    remarks: string;
};

export type TAnnex6V2CommonData = {
    id: string;
    /**
     * @description It's an ID for latest update for a document with given id.
     */
    _rev: string;
    marpolType: EMARPOLDocuments.ANNEX6V2;
    state: EAnnex6V2RecordState;
    timeZone?: string;
    submittedBy?: TAnnex6V2RecordUser;
    approvedBy?: TAnnex6V2RecordUser;
    verifiedBy?: TAnnex6V2RecordUser;
    strikedBy?: TAnnex6V2RecordUser;
    strikeOffReason?: string;
    editVersions?: TAnnex6V2Record[];
    dateOfApprovedRecordEdit?: string;
    _attachments?: object;
};

export type TAnnex6V2ObservedQuantity = {
    preOperation: number;
    postOperation: number;
    isHavingAutomaticCollection?: boolean;
};

export enum EAnnex6V2RecordCode {
    Annex6V2A1 = 'Annex6V2-A1',

    Annex6V2B1 = 'Annex6V2-B1',
    Annex6V2B2 = 'Annex6V2-B2',
    Annex6V2B3 = 'Annex6V2-B3',

    Annex6V2C1 = 'Annex6V2-C1',
    Annex6V2C2 = 'Annex6V2-C2',
    Annex6V2C3 = 'Annex6V2-C3',
    Annex6V2C4 = 'Annex6V2-C4',

    Annex6V2D1 = 'Annex6V2-D1',
    Annex6V2D2 = 'Annex6V2-D2',

    Annex6V2E1 = 'Annex6V2-E1',
    Annex6V2E2 = 'Annex6V2-E2',

    Annex6V2F1 = 'Annex6V2-F1',

    Annex6V2G1 = 'Annex6V2-G1',
    Annex6V2G2 = 'Annex6V2-G2',

    Annex6V2H1 = 'Annex6V2-H1',
    Annex6V2H2 = 'Annex6V2-H2',

    Annex6V2I1 = 'Annex6V2-I1',

    Annex6V2J1 = 'Annex6V2-J1',
    Annex6V2J2 = 'Annex6V2-J2',

    Annex6V2K1 = 'Annex6V2-K1',
    Annex6V2K2 = 'Annex6V2-K2',
    Annex6V2K3 = 'Annex6V2-K3',
    Annex6V2K4 = 'Annex6V2-K4',

    Annex6V2L1 = 'Annex6V2-L1',

    Annex6V2M1 = 'Annex6V2-M1',
    Annex6V2M2 = 'Annex6V2-M2',
}

export type TAnnex6V2Record =
    | TAnnex6V2GroupA
    | TAnnex6V2GroupB
    | TAnnex6V2GroupC
    | TAnnex6V2GroupD
    | TAnnex6V2GroupE
    | TAnnex6V2GroupG
    | TAnnex6V2GroupH
    | TAnnex6V2GroupI
    | TAnnex6V2GroupF
    | TAnnex6V2GroupJ
    | TAnnex6V2GroupK
    | TAnnex6V2GroupL
    | TAnnex6V2GroupM;

export type TAnnex6V2OperationAlert = {
    id: string;
    type:
        | 'WARNING'
        | 'ERROR'
        | 'INFO'
        | 'REQUIRED_FIELD'
        | 'OBSERVED_QUANTITY_ALERT';
    title: string;
    subtitle: string;
    message: string;
    listOfItems?: {
        title: string;
        subtitle?: string;
        message?: string;
        link?: string;
    }[];
    action?: {
        title: string;
        link: string;
    };
};

export enum EAnnex6V2EditMode {
    GENERAL = 'GENERAL',
    APPROVED_EDIT = 'APPROVED_EDIT',
}

export type TAnnex6V2BackendRecord = TAnnex6V2Record & {
    submittedBy: TAnnex6V2RecordUser;
    approvedBy?: TAnnex6V2RecordUser;
    verifiedBy?: TAnnex6V2RecordUser;
    strikedBy?: TAnnex6V2RecordUser;
    editedBy?: TAnnex6V2RecordUser;
    processedTanks: TAnnex6V2ProcessedTank[];
    nonParticipantTanks: { id: string; expectedQuantity: number }[];
    isAutoCreated?: boolean;
    isEditWarningVisible?: boolean;
};

export type TAnnex6V2ProcessedTank = TMARPOLProcessedTank;
export type TAnnex6V2CurrentOperation = {
    recordCode: EAnnex6V2RecordCode;
    operationState: EAnnex6V2OperationState;
    formData: TAnnex6V2Record;
    validationAlerts: IListObjectStore<TAnnex6V2OperationAlert>;
    processedTanks: IListObjectStore<TAnnex6V2ProcessedTank>;
    editMode?: EAnnex6V2EditMode;
};

export type TAnnex6V2HistoryFilters = {
    startDate: any;
    endDate: any;
    shownRecords: 'ALL' | 'ALL-PENDING';
    state?: EAnnex6V2RecordState | string;
    sortOrder?: 'asc' | 'desc';
   	vesselId?: string;
};

export type TAnnex6V2WeekReport = {
    totalRecords: number;
    pendingApprovalsCount: number;
    pendingVerificationsCount: number;
    strikedOffCount: number;
    lastSubmittedRecordCode?: EAnnex6V2RecordCode;
    lastSubmittedRecordDate?: string;
    isLastRecordStrikedOff: boolean;
    isItSundayFirstHalf: boolean;
    isItSundaySecondHalf: boolean;
    isWeekEndDatePassed: boolean;
    recordedOperations: {
        // [EAnnex6V2RecordCode.Annex6V2A]?: EAnnex6V2RecordState;
    };
};

export type bdnFilter = {
    startDate: string;
    endDate: string;
    sortOrder: string;
};

export interface IAnnex6V2Store extends IJobHOCState {
    records: IListObjectStore<TAnnex6V2BackendRecord>;
    currentOperation?: TAnnex6V2CurrentOperation;
    filter: TAnnex6V2HistoryFilters;
    currentWeekReport: TAnnex6V2WeekReport;
    previousWeekReport: TAnnex6V2WeekReport;
    bdnReports?: TAnnex6V2BunkerDeliveryNote[];
    bdnRecord?: TAnnex6V2BunkerDeliveryNote | undefined;
    bdnDisposalStatus?: TBDNDisposalStatus | undefined;
    bdnFilter: bdnFilter;
}

export type TBDNDisposalStatus = {
    isSampleDisposed: boolean;
    sampleDisposedDate: string;
    disposalMethod: string;
    fuelOilConsumedDate: string;
};

export type TAnnex6V2BunkerDeliveryNote = {
    id: string;
    _id: string;
    _rev?: string;
    attachmentSourceId?: string;
    attachmentSourceRev?: string;
    state: string;
    marpolType: EMARPOLDocuments.BDN;
    bunkerDeliveryNoteNo: string;
    sealNumber: string;
    fuelType: string;
    dateOfBunker?: string;
    tanks: {
        selectedTank: TMARPOLTank;
        addedQuantityInMT: number;
        totalRetainedQuantityInMT: number;
    }[];
    placeOrPosition:
        | {
              place: 'IN_PORT';
              portName: string;
          }
        | {
              place: 'AT_SEA';
              latitude: string;
              longitude: string;
          };
    totalBunkeredQty: number;
    sulphurContent?: number;
    remarks: string;
    selectedDate: string;
    timeZone?: string;
    dependentIds?: any[];
    disposalStatus: TBDNDisposalStatus;
    bdnAttachmentLink?: string;
};

export type TAnnex6V2CurrentOperationAlert = {
    id: string;
    type:
        | 'WARNING'
        | 'ERROR'
        | 'INFO'
        | 'REQUIRED_FIELD'
        | 'OBSERVED_QUANTITY_ALERT';
    title: string;
    subtitle: string;
    message: string;
    listOfItems?: {
        title: string;
        subtitle?: string;
        message?: string;
        link?: string;
    }[];
    action?: {
        title: string;
        link: string;
    };
};
